import React, { useState } from "react";
import styles from "./Calendar.module.scss";
import Button from "../Button/Button";
const toISO = (date) => date.toISOString().slice(0, 10);

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

export default function Calendar({ availableDates = [], onChange = () => {} }) {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [selected, setSelected] = useState(null);

  const availableSet = new Set(availableDates.map(toISO));
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = new Date(year, month, 1).getDay();
  const days = [];

  // Fill empty days at the start with previous month's days, clickable if available
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevMonthYear = month === 0 ? year - 1 : year;
  const prevMonthDays = getDaysInMonth(prevMonthYear, prevMonth);
  for (let i = 0; i < firstDay; i++) {
    const dayNum = prevMonthDays - firstDay + i + 1;
    const prevDate = new Date(prevMonthYear, prevMonth, dayNum);
    const iso = toISO(prevDate);
    const isAvailable = availableSet.has(iso);
    const isSelected = selected === iso;
    days.push(
      <button
        key={`prev-${i}`}
        className={[
          styles.day,
          isAvailable ? styles.emptyDaySelectable : styles.emptyDay,
          isSelected ? styles.selected : "",
        ].join(" ")}
        onClick={() => {
          if (isAvailable) {
            setSelected(iso);
            const formattedDate = `${prevDate.getFullYear()}/${String(
              prevDate.getMonth() + 1
            ).padStart(2, "0")}/${String(prevDate.getDate()).padStart(2, "0")}`;
            onChange(formattedDate);
          }
        }}
        disabled={!isAvailable}
        tabIndex={isAvailable ? 0 : -1}
      >
        {dayNum}
      </button>
    );
  }

  // Fill days of month
  for (let d = 1; d <= daysInMonth; d++) {
    const dayDate = new Date(year, month, d);
    const iso = toISO(dayDate);
    const isAvailable = availableSet.has(iso);
    const isSelected = selected === iso;
    days.push(
      <button
        key={d}
        className={[
          styles.day,
          isAvailable ? styles.available : styles.unavailable,
          isSelected ? styles.selected : "",
        ].join(" ")}
        onClick={() => {
          if (isAvailable) {
            setSelected(iso);
            const formattedDate = `${dayDate.getFullYear()}/${String(
              dayDate.getMonth() + 1
            ).padStart(2, "0")}/${String(dayDate.getDate()).padStart(2, "0")}`;
            onChange(formattedDate);
          }
        }}
        disabled={!isAvailable}
        tabIndex={isAvailable ? 0 : -1}
      >
        {d}
      </button>
    );
  }

  // Fill empty days at the end with next month's days, clickable if available
  const totalCells = firstDay + daysInMonth;
  const trailingDays = (7 - (totalCells % 7)) % 7;
  const nextMonth = month === 11 ? 0 : month + 1;
  const nextMonthYear = month === 11 ? year + 1 : year;
  for (let i = 1; i <= trailingDays; i++) {
    const nextDate = new Date(nextMonthYear, nextMonth, i);
    const iso = toISO(nextDate);
    const isAvailable = availableSet.has(iso);
    const isSelected = selected === iso;
    days.push(
      <button
        key={`next-${i}`}
        className={[
          styles.day,
          isAvailable ? styles.emptyDaySelectable : styles.emptyDay,
          isSelected ? styles.selected : "",
        ].join(" ")}
        onClick={() => {
          if (isAvailable) {
            setSelected(iso);
            const formattedDate = `${nextDate.getFullYear()}/${String(
              nextDate.getMonth() + 1
            ).padStart(2, "0")}/${String(nextDate.getDate()).padStart(2, "0")}`;
            onChange(formattedDate);
          }
        }}
        disabled={!isAvailable}
        tabIndex={isAvailable ? 0 : -1}
      >
        {i}
      </button>
    );
  }

  const prevMonthClick = () => {
    if (month === 0) {
      setMonth(11);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };
  const nextMonthClick = () => {
    if (month === 11) {
      setMonth(0);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Button
          className={styles.navBtn}
          icon="chevron_left"
          onClick={prevMonthClick}
        ></Button>
        <div className={styles.headerCenter}>
          {new Date(year, month).toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </div>
        <Button
          icon="chevron_right"
          className={styles.navBtn}
          onClick={nextMonthClick}
        ></Button>
      </div>

      <div className={styles.grid}>
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className={styles.dayLabel}>
            {d}
          </div>
        ))}
        {days}
      </div>
    </div>
  );
}
